<template>
  <v-card>
    <v-card-text>
      <v-row>
        <v-col cols="12" lg="6" class="statements-settings">
          <v-expansion-panels v-model="openPanels" multiple accordion>

            <!-- ============ Header ============ -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <div>
                  <h4>Header &amp; Names</h4>
                  <div class="text-caption grey--text">Title, county and treasurer name on the top stub</div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-radio-group v-model="titleSelection" row dense hide-details class="mt-0">
                  <template v-slot:label><span class="setting-label">Title</span></template>
                  <v-radio class="input-text-size" label="Taxpayer Copy" value="taxPayerCopy" />
                  <v-radio class="input-text-size" label="Tax Statement" value="taxStatement" />
                </v-radio-group>

                <div class="setting-row mt-3">
                  <span class="setting-label">County name color</span>
                  <v-color-picker
                    v-model="editItem.countyNameColor"
                    mode="hexa" hide-mode-switch hide-canvas hide-sliders hide-inputs show-swatches
                    :swatches="colorSwatches"
                    width="200"
                    class="color-picker-compact"
                  />
                </div>
                <div class="setting-row">
                  <span class="setting-label">Treasurer name color</span>
                  <v-color-picker
                    v-model="editItem.treasurerNameColor"
                    mode="hexa" hide-mode-switch hide-canvas hide-sliders hide-inputs show-swatches
                    :swatches="colorSwatches"
                    width="200"
                    class="color-picker-compact"
                  />
                </div>

                <v-checkbox
                  v-model="editItem.applyToInfoSheet"
                  class="input-text-size compact-check"
                  label="Use these colors on the Info Sheet too"
                  hide-details
                />
                <v-checkbox
                  v-model="editItem.highlightCountyName"
                  class="input-text-size compact-check"
                  label="Highlight county name"
                  hide-details
                />
                <v-checkbox
                  v-model="editItem.skipTreasurerNameOnTopStub"
                  class="input-text-size compact-check"
                  label="Leave the treasurer name off the top stub"
                  hide-details
                />
                <v-checkbox
                  v-model="editItem.showUsername"
                  class="input-text-size compact-check"
                  label="Print the username of whoever printed the statement"
                  hide-details
                />
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- ============ Codes ============ -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <div>
                  <h4>Barcodes, QR Codes &amp; Scanline</h4>
                  <div class="text-caption grey--text">{{ codesSummary }}</div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-simple-table dense class="codes-table">
                  <thead>
                    <tr>
                      <th>Stub</th>
                      <th>Code</th>
                      <th class="text-center">Scanline</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="position in stubPositions" :key="position">
                      <td>{{ position }}</td>
                      <td>
                        <v-btn-toggle
                          :value="codeFor(position)"
                          mandatory
                          dense
                          color="primary"
                          @change="(code) => setCode(position, code)"
                        >
                          <v-btn small value="none">None</v-btn>
                          <v-btn small value="barcode">Barcode</v-btn>
                          <v-btn small value="qr">QR code</v-btn>
                        </v-btn-toggle>
                      </td>
                      <td class="text-center">
                        <v-checkbox
                          :input-value="!!editItem[`printScanline${position}`]"
                          :disabled="!hasScanlineFormat"
                          hide-details
                          dense
                          class="d-inline-flex ma-0 pa-0"
                          :aria-label="`Scanline on ${position.toLowerCase()} stub`"
                          @change="(on) => setScanline(position, !!on)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
                <v-select
                  v-model="scanlineFormat"
                  :items="scanlineFormats"
                  label="Scanline format"
                  dense
                  outlined
                  hide-details
                  class="input-text-size mt-3"
                  style="max-width: 240px"
                />
                <div v-if="!hasScanlineFormat" class="text-caption grey--text mt-1">
                  Pick a scanline format to print scanlines on the stubs checked above.
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- ============ Property ============ -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <div>
                  <h4>Property Details</h4>
                  <div class="text-caption grey--text">Situs and legal description</div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-checkbox
                  v-model="editItem.appendAdditionToLegalDesc"
                  class="input-text-size compact-check"
                  label="Append the addition to the legal description"
                  hide-details
                />
                <v-checkbox
                  v-model="editItem.includeAcreageOrLotsInSitus"
                  class="input-text-size compact-check"
                  label="Include acreage / lots in the situs"
                  hide-details
                />
                <v-checkbox
                  v-model="editItem.includeSitusAndLegalOnAllStubs"
                  class="input-text-size compact-check"
                  label="Show situs and legal on the middle and bottom stubs"
                  hide-details
                />
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- ============ Payment stubs ============ -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <div>
                  <h4>Payment Stubs</h4>
                  <div class="text-caption grey--text">Due-date messages, text under the payment boxes, stub extras</div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="setting-subheading">Due-date messages</div>
                <v-row v-for="msg in dateMessages" :key="msg.msgKey" dense class="align-center">
                  <v-col cols="8">
                    <v-text-field
                      v-model="editItem[msg.msgKey]"
                      :label="msg.label"
                      class="input-text-size"
                      outlined
                      dense
                      hide-details
                    />
                  </v-col>
                  <v-col cols="4">
                    <ClarionDateControl
                      v-model="editItem[msg.dateKey]"
                      class="input-text-size"
                      :isRequired="false" :isIsoDate="true" :isToggle="false"
                      label="Date"
                    />
                  </v-col>
                </v-row>

                <div class="setting-subheading mt-4">Text under the payment box</div>
                <div v-for="box in underPayBoxes" :key="box.key" class="mb-3">
                  <div class="text-caption">{{ box.label }}</div>
                  <TextEditorField :placeholder="box.label" v-model="editItem[box.key]" />
                </div>

                <div class="setting-subheading mt-4">Options</div>
                <v-checkbox
                  v-model="editItem.includeDelinq"
                  class="input-text-size compact-check"
                  label="List other years with a balance (delinquents) on the bottom stub"
                  hide-details
                />
                <v-checkbox
                  v-model="editItem.noHalfPayAfterDec31"
                  class="input-text-size compact-check"
                  label="No half payments on prior years after December 31st"
                  hide-details
                />
                <div
                  v-for="option in textOptions"
                  :key="option.key"
                  class="d-flex align-center"
                >
                  <v-checkbox
                    v-model="editItem[option.key]"
                    class="input-text-size compact-check"
                    :label="option.label"
                    hide-details
                  />
                  <v-chip
                    v-if="isTextCustomized(option.key)"
                    x-small
                    label
                    color="primary"
                    text-color="white"
                    class="ml-2 mt-1"
                  >Custom wording</v-chip>
                  <v-btn
                    x-small
                    text
                    color="primary"
                    class="ml-1 mt-1"
                    :aria-label="`Edit the wording of ${option.label}`"
                    @click="openTextEditor(option.key)"
                  >
                    <v-icon x-small left>mdi-pencil</v-icon>Edit
                  </v-btn>
                </div>
                <div class="text-caption grey--text ml-8">
                  The address box and the delinquent notice print only when the bottom stub has no scanline.
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- ============ Middle section ============ -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <div>
                  <h4>Middle Section</h4>
                  <div class="text-caption grey--text">{{ middleModeSummary }}</div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-radio-group :value="middleMode" mandatory dense hide-details class="mt-0" @change="setMiddleMode">
                  <v-radio class="input-text-size" value="standard"
                    label="Standard: second-half stub or payment history, depending on the statement" />
                  <v-radio class="input-text-size" value="message" label="Print a message" />
                  <v-radio class="input-text-size" value="grid" label="Print an &quot;If paid by&quot; grid" />
                </v-radio-group>

                <v-checkbox
                  v-model="editItem.hideHeadingsInMiddle"
                  class="input-text-size compact-check mt-2"
                  label="Hide the heading above the middle message"
                  hide-details
                />

                <div class="setting-subheading mt-4">Middle messages</div>
                <div class="text-caption grey--text mb-2">
                  Used in message mode, and on statements that always get a message (low tax, some special assessments).
                </div>
                <div class="text-caption">Normal</div>
                <MiddleMessageSaveList
                  classes="mb-2"
                  placeholder="Middle Message.. (Normal)"
                  v-model="editItem.middleMsgForNormal"
                  :saved-messages="savedNormalMessages"
                  @save-message="(p) => handleSaveMessage('normal', p)"
                  @delete-message="(n) => handleDeleteMessage('normal', n)"
                />
                <div class="text-caption mt-3">Delinquent</div>
                <MiddleMessageSaveList
                  classes="mb-2"
                  placeholder="Middle Message.. (Delinquent)"
                  v-model="editItem.middleMsgForDelinquent"
                  :saved-messages="savedDelinquentMessages"
                  @save-message="(p) => handleSaveMessage('delinquent', p)"
                  @delete-message="(n) => handleDeleteMessage('delinquent', n)"
                />

                <div class="setting-subheading mt-4">&quot;If paid by&quot; grid</div>
                <v-row dense>
                  <v-col v-for="n in 3" :key="n" cols="4">
                    <ClarionDateControl
                      v-model="editItem[`printIfPaidByGridDate${n}`]"
                      class="input-text-size"
                      :isRequired="false" :isIsoDate="true" :isToggle="false"
                      :label="`Date ${n}`"
                    />
                  </v-col>
                </v-row>
                <v-checkbox
                  v-model="editItem.printGridAndMessage"
                  class="input-text-size compact-check"
                  label="Print the grid and a message together"
                  hide-details
                />
                <div class="text-caption grey--text ml-8 mb-2">This also leaves off the middle scanline.</div>
                <div class="text-caption">Message under the grid</div>
                <MiddleMessageSaveList
                  classes="mb-2"
                  placeholder="Middle Message.. (Under Grid)"
                  v-model="editItem.middleMsgForUnderGrid"
                  :saved-messages="savedUnderGridMessages"
                  @save-message="(p) => handleSaveMessage('underGrid', p)"
                  @delete-message="(n) => handleDeleteMessage('underGrid', n)"
                />
                <div class="text-caption mt-3">Message next to the grid</div>
                <MiddleMessageSaveList
                  classes="mb-2"
                  placeholder="Middle Message.. (Next to Grid)"
                  v-model="editItem.middleMsgForNextToGrid"
                  :saved-messages="savedNextToGridMessages"
                  @save-message="(p) => handleSaveMessage('nextToGrid', p)"
                  @delete-message="(n) => handleDeleteMessage('nextToGrid', n)"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- ============ Email ============ -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <div>
                  <h4>Email</h4>
                  <div class="text-caption grey--text">The email sent with emailed statements</div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-text-field class="input-text-size" label="Bcc email address (optional)"
                  dense v-model="editItem.bccEmailAddress" />
                <v-text-field class="input-text-size" label="Subject line"
                  dense v-model="editItem.subjectLine" />
                <v-textarea class="input-text-size" label="Body"
                  outlined rows="4" dense v-model="editItem.subjectBody" />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <div class="d-flex align-center save-bar">
            <span v-if="isDirty" class="text-caption warning--text">
              <v-icon small color="warning">mdi-circle-medium</v-icon>Unsaved changes. The preview shows them.
            </span>
            <v-spacer />
            <v-btn text :disabled="!isDirty" class="mr-2" @click="revert">Discard changes</v-btn>
            <v-btn depressed color="primary" @click="update">Save</v-btn>
          </div>
        </v-col>

        <v-col cols="12" lg="6">
          <StatementPreviewPane v-if="isLoaded" :statement-config="editItem" />
        </v-col>
      </v-row>
    </v-card-text>

    <v-dialog v-model="textEditor.open" max-width="560">
      <v-card v-if="textEditor.field">
        <v-card-title class="text-subtitle-1">Edit {{ textEditor.field.title }}</v-card-title>
        <v-card-text>
          <v-textarea
            v-model="textEditor.draft"
            :placeholder="textEditor.field.defaultText"
            :counter="textEditor.field.maxLength"
            :maxlength="textEditor.field.maxLength"
            :rules="[(v) => !v || v.length <= textEditor.field.maxLength || 'Too long to fit on the statement']"
            label="Wording printed on the statement"
            rows="3"
            outlined
            dense
            autofocus
            class="input-text-size"
          />
          <div v-if="textEditor.field.placeholders.length > 0" class="text-caption mb-2">
            <div v-for="ph in textEditor.field.placeholders" :key="ph.token">
              <code>{{ ph.token }}</code> prints {{ ph.meaning }}.
            </div>
          </div>
          <div class="text-caption grey--text">
            Leave blank to use the standard wording. The preview shows your change once you apply it.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn text small @click="textEditor.draft = ''">Use standard wording</v-btn>
          <v-spacer />
          <v-btn text small @click="textEditor.open = false">Cancel</v-btn>
          <v-btn color="primary" small depressed @click="applyTextEditor">Apply</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import TextEditorField from './TextEditorField.vue';
import MiddleMessageSaveList from './MiddleMessageSaveList.vue';
import StatementPreviewPane from './StatementPreviewPane.vue';
import ClarionDateControl from '../common/ClarionDateControl.vue';
import { clients } from '../../util/clients';
import { statementConfigScope } from '../../util/shared/system-config-scopes';
import { cloneItem } from '../../util/shared/vue-global';
import {
  STATEMENT_TEXT_FIELDS, STUB_POSITIONS, changedFields, isStatementTextCustomized,
  middleSectionMode, middleSectionModeFlags, scanlineNoneFlag, stubCode, stubCodeFlags,
} from '../../util/shared/statement-preview';

const { backendRest } = clients.direct;

const SAVED_MESSAGE_KEYS = [
  'savedNormalMessages',
  'savedDelinquentMessages',
  'savedUnderGridMessages',
  'savedNextToGridMessages',
];

export default {
  async mounted() {
    await this.loadConfig({ requiredScope: 'statementConfig' });
    await this.$nextTick();
    this.originalStatementSetting = cloneItem(this.editItem);
    this.savedNormalMessages = [...(this.editItem.savedNormalMessages || [])];
    this.savedDelinquentMessages = [...(this.editItem.savedDelinquentMessages || [])];
    this.savedUnderGridMessages = [...(this.editItem.savedUnderGridMessages || [])];
    this.savedNextToGridMessages = [...(this.editItem.savedNextToGridMessages || [])];
    this.isLoaded = true;
  },

  data: () => ({
    isLoaded: false,
    openPanels: [0],
    originalStatementSetting: null,
    savedNormalMessages: [],
    savedDelinquentMessages: [],
    savedUnderGridMessages: [],
    savedNextToGridMessages: [],
    stubPositions: STUB_POSITIONS,
    colorSwatches: [['#0000FF'], ['#FF0000'], ['#FFFF00']],
    scanlineFormats: [
      { text: 'None', value: 'none' },
      { text: 'Format 1', value: 'format1' },
      { text: 'Format 2', value: 'format2' },
      { text: 'Format 3', value: 'format3' },
    ],
    dateMessages: [
      { msgKey: 'msgForFirstHalfOrFull', dateKey: 'dateForFirstHalfOrFull', label: '1st half or full' },
      { msgKey: 'msgForSecondHalf', dateKey: 'dateForSecondHalf', label: '2nd half' },
      { msgKey: 'msgForFull', dateKey: 'dateForFull', label: 'Full / final' },
    ],
    textOptions: [
      { key: 'retainThisStub', label: 'Print "Retain this stub..."' },
      { key: 'includeAddressChangeBox', label: 'Include an address change box' },
      { key: 'printTaxBecomesDelinquent', label: 'Print "Tax becomes delinquent..."' },
    ],
    textEditor: {
      open: false,
      field: null,
      draft: '',
    },
    underPayBoxes: [
      { key: 'taxUnderPayBoxInMiddle', label: 'Middle stub' },
      { key: 'taxUnderPayBoxInBottom', label: 'Bottom stub' },
      { key: 'taxUnderPayBoxInFinal', label: 'Final (delinquent) stub' },
    ],
  }),

  components: {
    TextEditorField,
    MiddleMessageSaveList,
    StatementPreviewPane,
    ClarionDateControl,
  },

  computed: {
    ...mapState({
      statementSettingItem: (state) => state.SystemConfig.statementSettingItem || {},
    }),
    editItem() {
      return this.statementSettingItem;
    },
    isDirty() {
      if (!this.originalStatementSetting) return false;
      // Whole objects, not pickFields: some settings on this form were never in the field list.
      // changedFields skips the saved-message lists, which live in local data until saved.
      if (changedFields(this.originalStatementSetting, this.editItem).length > 0) return true;
      return SAVED_MESSAGE_KEYS.some((key) => JSON.stringify(this[key])
        !== JSON.stringify(this.originalStatementSetting[key] || []));
    },
    // Unset prints as "Taxpayer Copy" (printStatement.pdf.js only checks for 'taxStatement'),
    // so show it that way without writing a value until the user picks one.
    titleSelection: {
      get() {
        return this.editItem.titleSelection || 'taxPayerCopy';
      },
      set(value) {
        this.$set(this.editItem, 'titleSelection', value);
      },
    },
    // Unset means no scanline to the renderer, same as 'none'.
    scanlineFormat: {
      get() {
        return this.editItem.scanline || 'none';
      },
      set(value) {
        this.$set(this.editItem, 'scanline', value);
      },
    },
    hasScanlineFormat() {
      return !!this.editItem.scanline && this.editItem.scanline !== 'none';
    },
    middleMode() {
      return middleSectionMode(this.editItem);
    },
    middleModeSummary() {
      return {
        standard: 'Standard layout',
        message: 'Prints a message',
        grid: 'Prints an "If paid by" grid',
      }[this.middleMode];
    },
    codesSummary() {
      const parts = STUB_POSITIONS
        .map((p) => ({ p, code: stubCode(this.editItem, p) }))
        .filter(({ code }) => code !== 'none')
        .map(({ p, code }) => `${code === 'qr' ? 'QR' : 'Barcode'} on ${p.toLowerCase()}`);
      const scanlines = this.hasScanlineFormat
        ? STUB_POSITIONS.filter((p) => this.editItem[`printScanline${p}`]).map((p) => p.toLowerCase())
        : [];
      if (scanlines.length > 0) parts.push(`scanline on ${scanlines.join(', ')}`);
      return parts.length > 0 ? parts.join(' · ') : 'No codes or scanlines';
    },
  },

  methods: {
    ...mapActions('SystemConfig', [
      'loadConfig', 'updateConfig',
    ]),

    codeFor(position) {
      return stubCode(this.editItem, position);
    },
    setCode(position, code) {
      const flags = stubCodeFlags(this.editItem, position, code || 'none');
      Object.keys(flags).forEach((key) => this.$set(this.editItem, key, flags[key]));
    },
    setScanline(position, on) {
      this.$set(this.editItem, `printScanline${position}`, on);
      this.$set(this.editItem, 'printScanlineNone', scanlineNoneFlag(this.editItem));
    },
    setMiddleMode(mode) {
      const flags = middleSectionModeFlags(mode);
      Object.keys(flags).forEach((key) => this.$set(this.editItem, key, flags[key]));
    },
    isTextCustomized(optionKey) {
      return isStatementTextCustomized(this.editItem, optionKey);
    },
    openTextEditor(optionKey) {
      const field = STATEMENT_TEXT_FIELDS[optionKey];
      this.textEditor = {
        open: true,
        field,
        draft: this.isTextCustomized(optionKey) ? this.editItem[field.textKey].trim() : '',
      };
    },
    applyTextEditor() {
      const { field } = this.textEditor;
      const draft = (this.textEditor.draft || '').trim().slice(0, field.maxLength);
      // Typing the standard wording back in is the same as leaving it blank.
      const value = draft === field.defaultText ? '' : draft;
      // Don't turn "never set" into '' - that would flag unsaved changes for no change at all.
      if (value !== '' || this.editItem[field.textKey]) {
        this.$set(this.editItem, field.textKey, value);
      }
      this.textEditor.open = false;
    },
    revert() {
      if (!window.confirm('Discard your unsaved Statements changes?')) return;
      const original = cloneItem(this.originalStatementSetting);
      // Keys added since loading (first-time custom wording, a newly ticked option) have no
      // original value to restore, so remove them.
      Object.keys(this.editItem).forEach((key) => {
        if (!(key in original)) this.$delete(this.editItem, key);
      });
      Object.keys(original).forEach((key) => this.$set(this.editItem, key, original[key]));
      SAVED_MESSAGE_KEYS.forEach((key) => { this[key] = [...(original[key] || [])]; });
    },
    savedMessagesKey(type) {
      return {
        normal: 'savedNormalMessages',
        delinquent: 'savedDelinquentMessages',
        underGrid: 'savedUnderGridMessages',
        nextToGrid: 'savedNextToGridMessages',
      }[type];
    },
    handleSaveMessage(type, { name, content }) {
      const key = this.savedMessagesKey(type);
      const idx = this[key].findIndex((m) => m.name === name);
      if (idx >= 0) {
        this.$set(this[key], idx, { name, content });
      } else {
        this[key].push({ name, content });
      }
    },
    handleDeleteMessage(type, name) {
      const key = this.savedMessagesKey(type);
      this[key] = this[key].filter((m) => m.name !== name);
      this.autoSaveMessages();
    },
    // Deleting a saved message saves the message lists straight away, on top of the last
    // saved settings only. Other unsaved edits stay unsaved (Save / Discard still apply).
    async autoSaveMessages() {
      const pending = cloneItem(this.editItem);
      const savedLists = {};
      SAVED_MESSAGE_KEYS.forEach((key) => { savedLists[key] = this[key]; });
      const lastSaved = cloneItem(this.originalStatementSetting);
      const payload = {
        calledScope: 'statementConfig',
        item: {
          ...statementConfigScope.mergeForPersist(lastSaved),
          ...savedLists,
        },
        previousItem: this.originalStatementSetting,
        isLogAudit: true,
      };
      const ok = await this.updateConfig(payload);
      if (ok) {
        this.originalStatementSetting = cloneItem({ ...lastSaved, ...savedLists });
        // The store now holds the saved copy; put the unsaved edits back on top of it.
        Object.keys(pending).forEach((key) => {
          if (!SAVED_MESSAGE_KEYS.includes(key)) this.$set(this.editItem, key, pending[key]);
        });
        this.renderHTMLTextInDB();
      }
    },
    buildStatementConfigItem() {
      return statementConfigScope.pickFields(this.editItem);
    },
    mergeStatementConfigForSave() {
      return statementConfigScope.mergeForPersist(this.editItem);
    },
    async update() {
      if (window.confirm('Are you sure want to update this configuration?')) {
        this.editItem.pidValue = this.pidValue;
        const payload = {
          calledScope: 'statementConfig',
          item: {
            ...this.mergeStatementConfigForSave(),
            savedNormalMessages: this.savedNormalMessages,
            savedDelinquentMessages: this.savedDelinquentMessages,
            savedUnderGridMessages: this.savedUnderGridMessages,
            savedNextToGridMessages: this.savedNextToGridMessages,
          },
          previousItem: this.originalStatementSetting,
          isLogAudit: true,
        };
        const ok = await this.updateConfig(payload);
        if (ok) {
          this.originalStatementSetting = cloneItem(this.editItem);
        }

        // Call renderHTMLTextInDB after updating config
        this.renderHTMLTextInDB();
      }
    },
    async renderHTMLTextInDB() {
      const id = this.editItem.id || null;
      try {
        const response = await backendRest.get(`/jobs/render-html-text?id=${id}`);
        if (response.data.success) {
          return response;
        }
        return null;
      } catch (error) {
        return null;
      }
    },
  },
};
</script>

<style scoped>
/* The settings scroll inside their own column so the preview beside them stays in view.
   (position: sticky on the preview can't work here: v-tabs-items clips with overflow: hidden.) */
.statements-settings {
  max-height: calc(100vh - 210px);
  overflow-y: auto;
}

.save-bar {
  position: sticky;
  bottom: 0;
  z-index: 2;
  padding: 8px 0;
  background: white;
}

@media (max-width: 1263px) {
  .statements-settings {
    max-height: none;
    overflow-y: visible;
  }
}

.setting-label {
  font-weight: 600;
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.87);
}

.setting-row {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.setting-row .setting-label {
  min-width: 150px;
}

.setting-subheading {
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 4px;
}

.compact-check {
  margin-top: 4px;
  padding-top: 0;
}

.color-picker-compact {
  max-width: 300px !important;
}

.codes-table td,
.codes-table th {
  padding: 0 8px !important;
}
</style>
